/**
 * The new-patient registration the landing page offers as an example.
 *
 * Same bargain as the other examples: a chip is not a brief, so it is answered with something
 * prepared rather than with a minute of waiting and a bill. The medical history is deliberately a
 * short checkbox list, a real clinic's is longer, and making it theirs is the assistant's job.
 *
 * The JSON is compiled output, what the builder itself writes from these screens. It was
 * produced by running an authoring spec through flow-core's own `expandSpec` and `compile`
 * rather than typed by hand, and the test holds it to that: hydrate then compile must return it
 * unchanged, so it can never drift into something the builder would not have written.
 */
export const DENTAL_REGISTRATION = {
  id: 'dental-registration',
  brief: 'New patient registration for a dental clinic, with contact details and medical history',
  reply: 'Here is a three-step registration: their details, then the medical history a first appointment needs, then why they are coming in and whether to send reminders. Nothing here needs a receptionist to retype it. Tell me your practice name and I will make it yours, or send it to your phone and fill it in as a patient would.',
  name: 'New patient registration',
  flowJson: {
  "version": "7.3",
  "screens": [
    {
      "id": "screen_a",
      "title": "Your details",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextBody",
            "text": "A few questions before your first visit, so we are ready for you."
          },
          {
            "type": "TextInput",
            "name": "full_name",
            "label": "Full name",
            "input-type": "text",
            "required": true
          },
          {
            "type": "DatePicker",
            "name": "date_of_birth",
            "label": "Date of birth",
            "required": true
          },
          {
            "type": "TextInput",
            "name": "phone",
            "label": "Phone number",
            "input-type": "phone",
            "required": true
          },
          {
            "type": "TextInput",
            "name": "email",
            "label": "Email",
            "input-type": "email",
            "required": true
          },
          {
            "type": "Footer",
            "label": "Continue",
            "on-click-action": {
              "name": "navigate",
              "next": {
                "type": "screen",
                "name": "screen_b"
              }
            }
          }
        ]
      }
    },
    {
      "id": "screen_b",
      "title": "Medical history",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "CheckboxGroup",
            "name": "conditions",
            "label": "Anything we should know about?",
            "required": false,
            "data-source": [
              {
                "id": "heart",
                "title": "Heart condition"
              },
              {
                "id": "diabetes",
                "title": "Diabetes"
              },
              {
                "id": "allergy",
                "title": "Allergy to a medicine or anaesthetic"
              },
              {
                "id": "pregnant",
                "title": "Pregnant"
              },
              {
                "id": "none",
                "title": "None of these"
              }
            ]
          },
          {
            "type": "TextArea",
            "name": "medication",
            "label": "Medicines you take",
            "required": false,
            "helper-text": "Leave blank if none"
          },
          {
            "type": "Footer",
            "label": "Continue",
            "on-click-action": {
              "name": "navigate",
              "next": {
                "type": "screen",
                "name": "screen_c"
              }
            }
          }
        ]
      }
    },
    {
      "id": "screen_c",
      "title": "Almost done",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "RadioButtonsGroup",
            "name": "reason",
            "label": "What brings you in?",
            "required": true,
            "data-source": [
              {
                "id": "checkup",
                "title": "Routine check-up"
              },
              {
                "id": "pain",
                "title": "Pain or a problem"
              },
              {
                "id": "cosmetic",
                "title": "Cosmetic treatment"
              }
            ]
          },
          {
            "type": "OptIn",
            "name": "reminders",
            "label": "Send me appointment reminders on WhatsApp",
            "required": false
          },
          {
            "type": "Footer",
            "label": "Register me",
            "on-click-action": {
              "name": "complete",
              "payload": {
                "full_name": "${screen.screen_a.form.full_name}",
                "date_of_birth": "${screen.screen_a.form.date_of_birth}",
                "phone": "${screen.screen_a.form.phone}",
                "email": "${screen.screen_a.form.email}",
                "conditions": "${screen.screen_b.form.conditions}",
                "medication": "${screen.screen_b.form.medication}",
                "reason": "${form.reason}",
                "reminders": "${form.reminders}"
              }
            }
          }
        ]
      },
      "terminal": true
    }
  ],
  "routing_model": {
    "screen_a": [
      "screen_b"
    ],
    "screen_b": [
      "screen_c"
    ]
  }
},
};
